import { RevisionData } from '@tencent/slide-sdk-model/src/app/revision-data';
import type { MarkRequired } from 'ts-essentials';
import type { ICommandData } from '@tencent/slide-sdk-model';
import type { Command } from '@tencent/docs-connection';

export interface IUpSteamData {
    type: Command;
    baseRev: number;
    commands: ICommandData[];
    keydata: boolean;
    head: boolean;
    subId: string;
    oSubId: boolean;
    uuId: string;
    pver: number;
    taskId: number;
}

export interface IUserChange {
    taskId: IUpSteamData['taskId'];
    revision: IUpSteamData['baseRev'];
    commands: IUpSteamData['commands'];

    serialize(): IUpSteamData & { changeset: string };
    setStatus(status: UserChangeStatus): void;
    getStatus(): UserChangeStatus;
    setExpectedRev(expectedRev: number): void;
    getExpectedRev(): number | undefined;
    startDetectIsBlocked(blockCallback: () => any): void;
    stopDetectIsBlocked(): void;
    increaseBlockedRetryCount(): void;
    getBlockedRetryCount(): number;
}

export enum UserChangeStatus {
    Init,
    Sending,
    Retrying,
    BaseRevTooLow,
    Error,
    Accepted,
}

export class UserChange implements IUserChange {
    private status = UserChangeStatus.Init;
    private expectedRev?: number;
    private blockTimer?: NodeJS.Timeout;
    private blockedRetryCount = 0;

    constructor(private readonly upSteamData: IUpSteamData) {}

    public get taskId() {
        return this.upSteamData.taskId;
    }

    public get revision() {
        return this.upSteamData.baseRev;
    }

    public get commands() {
        return this.upSteamData.commands;
    }

    public serialize() {
        const validCommand = this.upSteamData.commands.filter(({ mutations }) => !!mutations?.length) as MarkRequired<
            ICommandData,
            'mutations'
        >[];
        const firstCommand: MarkRequired<ICommandData, 'mutations'>[] = [{ mutations: [] }];
        validCommand.forEach((command) => firstCommand[0].mutations.push(...command.mutations));
        const keyframe = RevisionData.fromCommandData(validCommand).toBase64String();
        const changeset = keyframe ? `${keyframe}\n` : '[]\n';
        const data = { ...this.upSteamData, changeset };
        Reflect.deleteProperty(data, 'commands');
        return data;
    }

    public setStatus(status: UserChangeStatus) {
        this.status = status;
    }

    public getStatus() {
        return this.status;
    }

    public getExpectedRev() {
        return this.expectedRev;
    }

    public setExpectedRev(expectedRev: number) {
        this.expectedRev = expectedRev;
    }

    public startDetectIsBlocked(blockCallback: () => void) {
        this.stopDetectIsBlocked();
        this.blockTimer = setTimeout(() => {
            if (this.status === UserChangeStatus.Sending) {
                blockCallback();
                return;
            }
        }, 75000);
    }

    public stopDetectIsBlocked() {
        if (this.blockTimer !== undefined) {
            clearTimeout(this.blockTimer);
            this.blockTimer = undefined;
        }
    }

    public increaseBlockedRetryCount() {
        this.blockedRetryCount += 1;
    }

    public getBlockedRetryCount() {
        return this.blockedRetryCount;
    }
}
